import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send, AlertCircle, Headphones, DollarSign, Handshake, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Floating chat widget — works for both exec and brand portals.
 * Props:
 *   audience: 'executive' | 'brand'
 *   getHeaders: () => ({ Authorization: 'Bearer ...' })
 *   contactEmail?: string
 */
export default function AIChatWidget({ audience, getHeaders, contactEmail }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [escalation, setEscalation] = useState(null); // {queue, summary}
  const [showEscalate, setShowEscalate] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        text: audience === 'executive'
          ? "Hi, I'm Freddy — your in-app guide. Ask me anything: how to accept a campaign, write a great post, get paid faster, or fix a glitch. I can also connect you with our team."
          : "Hi, I'm Freddy — your in-app guide. Ask me anything: campaign setup, picking the right offering, billing, deliverable reviews. I can also connect you with Sales for managed plans, Partnerships, or Technical Support."
      }]);
    }
  }, [open, audience, messages.length]);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput('');
    setMessages(m => [...m, { role: 'user', text }]);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/chat/message`,
        { message: text, session_id: sessionId },
        { headers: getHeaders() }
      );
      setSessionId(data.session_id);
      setMessages(m => [...m, { role: 'assistant', text: data.reply }]);
      if (data.escalation) {
        setEscalation(data.escalation);
        setShowEscalate(true);
      }
    } catch (e) {
      const detail = e.response?.data?.detail || 'Chat is unavailable. Please email support@freddyleads.com.';
      setMessages(m => [...m, { role: 'assistant', text: detail, error: true }]);
    } finally { setSending(false); }
  };

  const escalate = async (queue) => {
    try {
      const summary = escalation?.summary || (messages.find(m => m.role === 'user')?.text || '').slice(0, 200);
      const { data } = await axios.post(`${API}/chat/escalate`,
        { session_id: sessionId, queue, summary, contact_email: contactEmail },
        { headers: getHeaders() }
      );
      toast.success(`Connected. Ticket ID: ${data.id.slice(0, 14)} — our team will be in touch within 1 business day.`);
      setMessages(m => [...m, {
        role: 'assistant',
        text: `Ticket ${data.id.slice(0, 14)}… created for our **${queue}** team. They have your chat transcript and will reach out${contactEmail ? ` at ${contactEmail}` : ''}.`,
      }]);
      setShowEscalate(false);
      setEscalation(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create ticket');
    }
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="ai-chat-fab"
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#FF4343] text-[#090E33] shadow-lg shadow-[#FF4343]/30 hover:scale-110 transition-transform flex items-center justify-center">
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-3rem)] bg-[#0a1138] border border-white/10 shadow-2xl flex flex-col" data-testid="ai-chat-widget">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div>
              <div className="text-sm font-semibold text-white">Ask <span className="text-[#FF4343]">Freddy</span></div>
              <div className="text-[10px] text-[#6B7280]">In-app help · always on</div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="ai-chat-close" className="text-[#9CA3AF] hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollerRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ai-chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-3 py-2 text-xs whitespace-pre-wrap leading-relaxed ${
                  m.role === 'user' ? 'bg-[#FF4343] text-[#090E33] font-medium'
                    : m.error ? 'bg-red-500/10 border border-red-500/30 text-red-300'
                      : 'bg-white/5 border border-white/10 text-[#9CA3AF]'
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white/5 border border-white/10 px-3 py-2 text-xs text-[#6B7280]">Freddy is typing…</div>
              </div>
            )}

            {/* Escalation suggestion */}
            {showEscalate && (
              <div className="bg-[#FF4343]/10 border border-[#FF4343]/30 p-3 space-y-2" data-testid="ai-chat-escalation-prompt">
                <p className="text-[10px] font-mono uppercase tracking-wider text-[#FF4343] flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Want to talk to a human?</p>
                <div className="grid grid-cols-2 gap-1.5">
                  <button onClick={() => escalate('technical')} data-testid="escalate-technical" className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 px-2 py-1.5 text-[10px] text-white">
                    <Headphones className="w-3 h-3 text-[#FF4343]" /> Technical
                  </button>
                  <button onClick={() => escalate('sales')} data-testid="escalate-sales" className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 px-2 py-1.5 text-[10px] text-white">
                    <DollarSign className="w-3 h-3 text-[#FF4343]" /> Sales
                  </button>
                  <button onClick={() => escalate('partnership')} data-testid="escalate-partnership" className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 px-2 py-1.5 text-[10px] text-white">
                    <Handshake className="w-3 h-3 text-[#FF4343]" /> Partnership
                  </button>
                  <button onClick={() => escalate('general')} data-testid="escalate-general" className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 px-2 py-1.5 text-[10px] text-white">
                    <ChevronRight className="w-3 h-3 text-[#FF4343]" /> General
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-white/10 p-3 flex gap-2">
            <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()}
              data-testid="ai-chat-input"
              placeholder="Type your question..."
              className="flex-1 px-3 py-2 bg-[#090E33] border border-white/10 text-white text-xs placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
            <button onClick={send} disabled={!input.trim() || sending} data-testid="ai-chat-send"
              className="px-3 py-2 bg-[#FF4343] text-[#090E33] disabled:opacity-50 hover:bg-[#E03030]">
              <Send className="w-4 h-4" />
            </button>
          </div>

          {/* Footer escalation shortcut (always visible) */}
          {!showEscalate && (
            <button onClick={() => setShowEscalate(true)} data-testid="ai-chat-talk-to-human"
              className="text-[10px] uppercase font-mono text-[#6B7280] hover:text-[#FF4343] py-1.5 border-t border-white/5">
              Talk to a human →
            </button>
          )}
        </div>
      )}
    </>
  );
}
